import {
    type IsoAndUnixTimestamp,
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import { idZodSchema } from "@@/shared/id";
import { type PhoneNumber, phoneNumberZodSchema } from "@@/shared/phone";
import {
    type StorageItemId,
    storageItemIdZodSchema,
} from "@@/shared/storage-items/storage-item-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { type ProviderId, providerIdZodSchema } from "../provider-id";

type ProviderEditCommand = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "PROVIDER_EDIT_COMMAND";

    readonly providerId: ProviderId;
    readonly name?: string;
    readonly subtitle?: string;
    readonly description?: string;
    readonly areas?: Record<string, boolean>;
    readonly publicEmail?: string;
    readonly orderEmail?: string;
    readonly prepTime?: DurationInMinutes;
    readonly category?: string;
    readonly publicPhone?: {
        readonly countryCode: string;
        readonly phoneNumber: PhoneNumber;
    };
    readonly orderPhone?: {
        readonly countryCode: string;
        readonly phoneNumber: PhoneNumber;
    };
    readonly address?: {
        readonly street: string;
        readonly city: string;
        readonly zipCode: string;
        readonly country: string;
    };
    readonly logoImage?: StorageItemId;
    readonly posterImage?: StorageItemId;
};

type ProviderEditCommandFactoryParams = Except<
    ProviderEditCommand,
    "_id" | "_type" | "_createdAt"
>;

function providerEditCommandFactory(
    params: ProviderEditCommandFactoryParams,
): ProviderEditCommand {
    return {
        ...params,
        _id: generateId("provider_edit_command__"),
        _type: "PROVIDER_EDIT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
}

const providerEditCommandZodSchema: z.ZodSchema<ProviderEditCommand> = z.object(
    {
        _id: idZodSchema,
        _createdAt: isoAndUnixZodSchema,
        _type: z.literal("PROVIDER_EDIT_COMMAND"),
        name: z.string().optional(),
        subtitle: z.string().optional(),
        description: z.string().optional(),
        providerId: providerIdZodSchema,
        address: z
            .object({
                street: z.string(),
                city: z.string(),
                zipCode: z.string(),
                country: z.string(),
            })
            .optional(),
        areas: z.record(z.string(), z.boolean()).optional(),
        category: z.string().optional(),
        publicEmail: z.string().email().optional(),
        orderEmail: z.string().email().optional(),
        logoImage: storageItemIdZodSchema.optional(),
        posterImage: storageItemIdZodSchema.optional(),
        prepTime: z
            .number()
            .optional() as unknown as z.ZodSchema<DurationInMinutes>,
        publicPhone: z
            .object({
                countryCode: z.string(),
                phoneNumber: phoneNumberZodSchema,
            })
            .optional(),
        orderPhone: z
            .object({
                countryCode: z.string(),
                phoneNumber: phoneNumberZodSchema,
            })
            .optional(),
    },
);

export { providerEditCommandFactory, providerEditCommandZodSchema };
export type { ProviderEditCommand, ProviderEditCommandFactoryParams };
